import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { setLanguage } from '@/i18n';
import type { Language } from '@/i18n';
import {
  isSwitcherEnabled,
  tenantFallbackLanguage,
  tenantLanguages,
  toTenantSupported,
} from '@/i18n/language';

/**
 * A language names itself the same way whatever language you are reading in, so
 * these are endonyms rather than catalogue keys. The flag is decoration — the
 * endonym is what assistive tech announces.
 */
const LANGUAGES: Record<Language, { flag: string; code: string; name: string }> = {
  en: { flag: '🇬🇧', code: 'EN', name: 'English' },
  fr: { flag: '🇫🇷', code: 'FR', name: 'Français' },
  nl: { flag: '🇳🇱', code: 'NL', name: 'Nederlands' },
};

/**
 * Overlays that take the screen over, and must own it alone.
 *
 * Radix dialogs (every modal in `components/modals`, plus the `sheet` and
 * `drawer` primitives) mark themselves `role="dialog"` + `data-state="open"`,
 * and `alert-dialog` uses `role="alertdialog"` instead.
 * `MarketingConsentModal` is hand-rolled and renders inline with
 * `aria-modal="true"` rather than portalling, so it needs the last clause.
 */
const OVERLAY_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[aria-modal="true"]',
].join(', ');

/**
 * Screens that present as a takeover with their own chrome and back control.
 * `PickEmApp` routes these, so the path is the signal.
 */
const TAKEOVER_PATHS = ['/how-to-play'];

/**
 * Whether a modal or takeover screen currently owns the display.
 *
 * The switcher belongs to the main screens' header, not on top of whatever is
 * covering them, so it steps aside for both. Route changes go through
 * `pushState`, which fires no event, but they do swap the screen underneath —
 * so the same observer that watches for dialogs also catches them, and the
 * path is re-read on every recompute.
 */
const useDisplayOwnedByOverlay = (): boolean => {
  const [owned, setOwned] = useState(false);

  useEffect(() => {
    let frame = 0;

    const compute = () => {
      frame = 0;
      const overlayOpen = !!document.querySelector(OVERLAY_SELECTOR);
      const onTakeover = TAKEOVER_PATHS.some(path => window.location.pathname.endsWith(path));
      setOwned(overlayOpen || onTakeover);
    };

    // Coalesced: a single screen swap fires many mutations, and this only ever
    // needs to settle once per frame.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(compute);
    };

    compute();

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state', 'aria-modal'],
    });
    window.addEventListener('popstate', schedule);

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return owned;
};

/**
 * A body-level host for the switcher, held outside the app's own subtree.
 *
 * `AppContent` and `AuthProvider` each return early in their loading, error and
 * pre-auth branches, and the top-level boundary replaces the tree outright when
 * anything below it throws. Hosted here the switcher survives all of them.
 *
 * It also keeps the host out of the `aria-hidden` Radix stamps on `#root`, so
 * the control is never half-present — it is either properly there or, while an
 * overlay owns the screen, not rendered at all.
 */
const useSwitcherHost = (): HTMLElement | null => {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const element = document.createElement('div');
    element.id = 'language-switcher-host';
    document.body.appendChild(element);

    const unhide = () => {
      element.removeAttribute('aria-hidden');
      element.removeAttribute('data-aria-hidden');
    };

    unhide();
    const observer = new MutationObserver(unhide);
    observer.observe(element, { attributeFilter: ['aria-hidden', 'data-aria-hidden'] });

    setHost(element);

    return () => {
      observer.disconnect();
      element.remove();
    };
  }, []);

  return host;
};

/**
 * The language switcher, fixed to the top-right of every screen.
 *
 * It mounts outside the app's error boundary (see `AppChrome`) so it survives
 * the pre-auth, loading, error and crash states that replace the tree, which
 * means it may use nothing but i18n — already initialised in `main.tsx` before
 * the first render.
 *
 * Layering: modals and sheets sit at `z-50`, the marketing consent modal at
 * `z-[60]`, toasts at `z-[100]`. The ticket asks for a control no modal or
 * gate can hide, so this sits at `z-[70]` — above every blocking surface,
 * below toasts — and in a host that keeps it interactive there (see
 * `useSwitcherHost`).
 *
 * Which languages it offers, and whether it renders at all, come from the
 * tenant: GAM-729 is Circus Daily's, and DraftKings and Skypicks must not grow
 * a switcher for content they do not have.
 */
export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const host = useSwitcherHost();
  const enabled = isSwitcherEnabled();
  const overlayOwnsDisplay = useDisplayOwnedByOverlay();
  const languages = tenantLanguages();

  // Narrowed to the tenant, not just to what the app ships: a tenant without
  // English would otherwise show an "EN" pill for a language it does not offer,
  // and `activeIndex` of -1 would focus nothing when the menu opened.
  const active =
    toTenantSupported(i18n.resolvedLanguage ?? i18n.language) ?? tenantFallbackLanguage();
  const activeIndex = languages.indexOf(active);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Opening moves focus onto the language already in use, so arrowing starts there.
  useEffect(() => {
    if (open) optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  const choose = (language: Language) => {
    setLanguage(language);
    close();
  };

  const onOptionKeyDown = (event: React.KeyboardEvent, index: number) => {
    const { length } = languages;
    const moveTo = (next: number) => {
      event.preventDefault();
      optionRefs.current[(next + length) % length]?.focus();
    };

    if (event.key === 'ArrowDown') moveTo(index + 1);
    else if (event.key === 'ArrowUp') moveTo(index - 1);
    else if (event.key === 'Home') moveTo(0);
    else if (event.key === 'End') moveTo(length - 1);
    else if (event.key === 'Escape') close();
    else if (event.key === 'Tab') close(false);
  };

  if (!enabled || !host || overlayOwnsDisplay) return null;

  return createPortal(
    <div
      ref={containerRef}
      // Language names and codes are not translatable content. Left to itself,
      // Chrome's auto-translate reads the "EN" pill as the Dutch and French
      // word "en" and renders it "and" — garbling the one control a player
      // uses to get out of a language they cannot read.
      translate="no"
      // Load-bearing, and inline rather than a utility class: an open Radix
      // dialog puts `pointer-events: none` on <body>, which this would
      // otherwise inherit, and an inline style cannot lose to stylesheet
      // ordering or specificity.
      style={{ pointerEvents: 'auto' }}
      // `absolute`, not `fixed`: `AppHeader` is static and scrolls away, so a
      // fixed pill stayed pinned to the viewport and floated over the questions
      // once the header had gone. Absolute keeps it sitting in the header and
      // scrolling with it.
      //
      // `top-1.5` centres the 36px pill in that 48px bar (6px above and below).
      // At `top-3` its bottom landed exactly on 48px, flush against the header's
      // lower edge with no breathing room — worst on mobile, where the bar is
      // tightest.
      className="notranslate pointer-events-auto absolute right-3 top-1.5 z-[70]"
    >
      <button
        ref={triggerRef}
        type="button"
        // aria-label overrides the visible EN/FR/NL, so it must name the
        // language itself or a screen-reader user never learns which is on.
        aria-label={`${t('language.label')}: ${LANGUAGES[active].name}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className="flex h-9 items-center gap-1.5 rounded-full border border-white/25 bg-black/60 px-3 text-xs font-semibold uppercase text-white backdrop-blur-sm transition-transform duration-150 active:scale-95"
      >
        <span aria-hidden>{LANGUAGES[active].flag}</span>
        <span>{LANGUAGES[active].code}</span>
        <span aria-hidden className="text-[9px] leading-none opacity-70">
          ▼
        </span>
      </button>

      {open && (
        // A listbox must own its options directly — no <ul>/<li> in between.
        <div
          role="listbox"
          aria-label={t('language.label')}
          // No `aria-activedescendant`: focus really moves onto the options
          // below, so the attribute would have to track the *focused* one. It
          // named the selected one instead, which contradicted DOM focus the
          // moment a player arrowed off it.
          className="absolute right-0 mt-1.5 min-w-[7rem] overflow-hidden rounded-xl border border-white/20 bg-black/90 py-1 shadow-lg backdrop-blur-sm"
        >
          {languages.map((language, index) => {
            const { flag, code, name } = LANGUAGES[language];
            const selected = language === active;

            return (
              <button
                key={language}
                id={`language-option-${language}`}
                ref={element => {
                  optionRefs.current[index] = element;
                }}
                type="button"
                role="option"
                aria-label={name}
                aria-selected={selected}
                onClick={() => choose(language)}
                onKeyDown={event => onOptionKeyDown(event, index)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-white transition-colors ${
                  selected ? 'bg-white/15' : 'hover:bg-white/10'
                }`}
              >
                <span aria-hidden>{flag}</span>
                <span aria-hidden>{code}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>,
    host
  );
}
